import axiosInstance from '../api/axiosInstance'
import { 
  MercadoLibreTokens, 
  MercadoLibreTestResponse, 
  MercadoLibreProduct, 
  MercadoLibreFilters, 
  MercadoLibreSyncResponse,
  MercadoLibreUser,
  MercadoLibreOAuthResponse,
  MercadoLibreOAuthCallback,
  MercadoLibreTokenStatus
} from '../components/integracion-mercadolibre/types/mercadolibre'

class MercadoLibreService {
  /**
   * Guarda los tokens de MercadoLibre
   */
  async saveTokens(tokens: MercadoLibreTokens): Promise<void> {
    const response = await axiosInstance.post('/settings/mercadolibre-tokens', tokens)
    return response.data
  }

  /**
   * Obtiene los tokens de MercadoLibre guardados
   */
  async getTokens(): Promise<MercadoLibreTokens> {
    const response = await axiosInstance.get('/settings/mercadolibre-tokens')
    return response.data
  }

  /**
   * Prueba la conexión con MercadoLibre usando los tokens
   */
  async testConnection(): Promise<MercadoLibreTestResponse> {
    const response = await axiosInstance.post('/settings/mercadolibre-tokens/test')
    return response.data
  }

  /**
   * Refresca el access token usando el refresh token
   */
  async refreshAccessToken(): Promise<{ accessToken: string }> {
    const response = await axiosInstance.post('/settings/mercadolibre-tokens/refresh')
    return response.data
  }

  /**
   * Obtiene información del usuario de MercadoLibre
   */
  async getUserInfo(): Promise<MercadoLibreUser> {
    const response = await axiosInstance.get('/mercadolibre/user-info')
    return response.data
  }

  /**
   * 1. Búsqueda global de productos en MercadoLibre
   * GET /api/mercadolibre/products/search
   */
  async searchProducts(params?: {
    q?: string
    category_id?: string
    brand?: string
    price?: string
    condition?: 'new' | 'used' | 'not_specified'
    shipping_cost?: 'free' | 'paid'
    sort?: 'price_asc' | 'price_desc' | 'relevance'
    limit?: number
    offset?: number
    site_id?: string
  }): Promise<{
    success: boolean
    data: {
      results: MercadoLibreProduct[]
      paging: {
        total: number
        offset: number
        limit: number
      }
    }
    message: string
  }> {
    const response = await axiosInstance.get('/mercadolibre/products/search', { params })
    return response.data
  }

  /**
   * 2. Lista de productos del vendedor
   * GET /api/mercadolibre/products/seller
   */
  async getSellerProducts(params?: {
    limit?: number
    offset?: number
    status?: 'active' | 'paused' | 'closed'
    sort?: 'price_asc' | 'price_desc' | 'relevance'
  }): Promise<{
    success: boolean
    data: {
      results: MercadoLibreProduct[]
      paging: {
        total: number
        offset: number
        limit: number
      }
    }
    message: string
  }> {
    const response = await axiosInstance.get('/mercadolibre/products/seller', { params })
    return response.data
  }

  /**
   * 3. Búsqueda en productos del vendedor
   * GET /api/mercadolibre/products/seller-search
   */
  async searchSellerProducts(params?: {
    q?: string
    seller_sku?: string
    status?: 'active' | 'paused' | 'closed'
    limit?: number
    offset?: number
    sort?: 'price_asc' | 'price_desc' | 'relevance'
  }): Promise<{
    success: boolean
    data: {
      results: MercadoLibreProduct[]
      paging: {
        total: number
        offset: number
        limit: number
      }
    }
    message: string
  }> {
    const response = await axiosInstance.get('/mercadolibre/products/seller-search', { params })
    return response.data
  }

  /**
   * 4. Buscar producto por SKU
   * GET /api/mercadolibre/products/sku/:sku
   */
  async getProductBySku(sku: string): Promise<{
    success: boolean
    data: {
      results: MercadoLibreProduct[]
    }
    message: string
  }> {
    const response = await axiosInstance.get(`/mercadolibre/products/sku/${sku}`)
    return response.data
  }

  /**
   * 5. Obtener producto específico por ID
   * GET /api/mercadolibre/products/:itemId
   */
  async getProductById(itemId: string): Promise<{
    success: boolean
    data: MercadoLibreProduct
    message: string
  }> {
    const response = await axiosInstance.get(`/mercadolibre/products/${itemId}`)
    return response.data
  }

  /**
   * Método legacy - mantener compatibilidad
   * @deprecated Usar getSellerProducts() en su lugar
   */
  async getProducts(params?: MercadoLibreFilters): Promise<{
    results: MercadoLibreProduct[]
    paging: {
      total: number
      offset: number
      limit: number
    }
  }> {
    const response = await axiosInstance.get('/mercadolibre/products', { params })
    return response.data
  }

  /**
   * Sincroniza productos con MercadoLibre
   */
  async syncProducts(productIds: string[]): Promise<MercadoLibreSyncResponse> {
    const response = await axiosInstance.post('/mercadolibre/sync-products', { productIds })
    return response.data
  }

  /**
   * Obtiene la URL de autorización OAuth
   */
  async getOAuthAuthUrl(): Promise<MercadoLibreOAuthResponse> {
    const response = await axiosInstance.get('/mercadolibre/oauth/auth')
    return response.data
  }

  /**
   * Procesa el callback de OAuth con el código de autorización
   */
  async processOAuthCallback(code: string): Promise<MercadoLibreOAuthCallback> {
    const response = await axiosInstance.post('/mercadolibre/oauth/callback', { code })
    return response.data
  }

  /**
   * Obtiene el estado actual del token de MercadoLibre
   */
  async getTokenStatus(): Promise<MercadoLibreTokenStatus> {
    const response = await axiosInstance.get('/mercadolibre/token-status')
    return response.data
  }
}

const mercadolibreService = new MercadoLibreService()
export default mercadolibreService
